import {
  CaretDownOutlined,
  CaretUpOutlined,
  LockOutlined,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { DiscountRow } from "./itemDiscountMath";
import { formatQuantity } from "./itemDiscountMath";
import { RowPercentOptions, type RowPercentChoice } from "./RowPercentOptions";

export interface ItemDiscountRowProps {
  row: DiscountRow;
  /** Колко бройки от реда са избрани за отстъпката (0 = редът е без отстъпка). */
  selectedQty: number;
  /** Процентът, който избраните бройки ще получат (собственият или общият). */
  percent: number;
  /** Редът е със собствен процент — общият не го пипа. */
  hasOwnPercent: boolean;
  generalPercent: number;
  expanded: boolean;
  onToggleExpanded: () => void;
  onPickPercent: (choice: RowPercentChoice) => void;
  onQuantityChange: (delta: number) => void;
  lockedHint: string;
  customPriceHint: string;
  isNarrow: boolean;
}

/** Ред в модала „Отстъпки": име, цена по менюто, chip с процента на реда
 *  (кликът разгръща „За този ред") и степер −N/max+ за колко бройки да го
 *  получат. Сменя ли се сегашната отстъпка, тя стои задраскана пред chip-а.
 *  Заключена цена → катинар, без chip и степер. */
export const ItemDiscountRow = ({
  row,
  selectedQty,
  percent,
  hasOwnPercent,
  generalPercent,
  expanded,
  onToggleExpanded,
  onPickPercent,
  onQuantityChange,
  lockedHint,
  customPriceHint,
  isNarrow,
}: ItemDiscountRowProps) => {
  const { t } = useTranslation();
  const active = selectedQty > 0;
  // Активният избор в лентата: неизбран ред е „Без", а не „Обща" — модалът
  // показва крайното състояние.
  const activeChoice: RowPercentChoice = !active
    ? 0
    : hasOwnPercent
      ? percent
      : null;

  // Какво ще стане с реда при запис: сегашната отстъпка се задрасква, щом
  // процентът се сменя или отпада („−10% → −15%", „−10% → Без"). Частично
  // количество със същия процент не се задрасква — остатъкът личи по степера.
  const newPercent = active ? percent : 0;
  const previousPercent =
    row.currentPercent > 0 && row.currentPercent !== newPercent
      ? row.currentPercent
      : null;
  const removesDiscount = previousPercent !== null && newPercent === 0;
  // Положителен процент замества собствената цена (едно поле в BE).
  const replacesCustomPrice = row.hasCustomPrice && newPercent > 0;

  const percentChip = !row.locked && (
    <span className="inline-flex shrink-0 items-center gap-1.5">
      {previousPercent !== null && (
        <>
          <span
            title={t("itemDiscounts.currentPercentTitle", {
              percent: previousPercent,
            })}
            className={`font-pos-mono text-[12px] font-semibold line-through ${
              removesDiscount ? "text-pos-danger-ink" : "text-pos-muted"
            }`}
          >
            −{previousPercent}%
          </span>
          <span className="text-[12px] text-pos-muted">→</span>
        </>
      )}
      <PercentChip
        selected={active}
        percent={percent}
        hasOwnPercent={hasOwnPercent}
        removesDiscount={removesDiscount}
        expanded={expanded}
        onClick={onToggleExpanded}
        noneLabel={t("itemDiscounts.none")}
        ownSuffix={t("itemDiscounts.ownSuffix")}
      />
    </span>
  );
  const customPriceTag = row.hasCustomPrice && (
    <span
      className={`shrink-0 rounded-md bg-pos-addon-bg px-[6px] text-[10.5px] font-semibold text-pos-addon-ink ${
        replacesCustomPrice ? "line-through opacity-70" : ""
      }`}
    >
      {customPriceHint}
    </span>
  );

  return (
    <div
      className={`rounded-[12px] border transition-colors ${
        row.locked
          ? "border-pos-line3 bg-pos-panel opacity-60"
          : active
            ? "border-pos-accent/30 bg-pos-marked"
            : "border-pos-line3 bg-pos-panel"
      }`}
      title={row.locked ? lockedHint : undefined}
    >
      <div className="flex items-center justify-between gap-2.5 px-3 py-2.5">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 truncate text-[14px] font-bold leading-[1.2] text-pos-ink">
              {row.name}
            </span>
            <span className="shrink-0 font-pos-mono text-[12px] text-pos-muted">
              {row.unitPrice.toFixed(2)}
            </span>
            {!isNarrow && percentChip}
            {!isNarrow && customPriceTag}
          </div>
          {/* Телефон: chip-ът е на собствен ред, иначе името се реже до 2–3 букви */}
          {isNarrow && (percentChip || customPriceTag) && (
            <div className="flex items-center gap-2">
              {percentChip}
              {customPriceTag}
            </div>
          )}
          {row.additionLabels.length > 0 && (
            <span className="truncate text-[11px] text-pos-soft">
              <span className="text-pos-disc-ink">＋</span>{" "}
              {row.additionLabels.join(", ")}
            </span>
          )}
        </div>

        {row.locked ? (
          <span className="grid h-[30px] w-[30px] shrink-0 place-items-center text-[13px] text-pos-faint">
            <LockOutlined />
          </span>
        ) : (
          <div className="flex shrink-0 items-center gap-1">
            <QtyButton
              icon={<MinusOutlined />}
              onClick={() => onQuantityChange(-1)}
              disabled={selectedQty === 0}
            />
            {/* Избраното количество, а под него „от N" — като в „Сторно" */}
            <div className="flex min-w-[48px] flex-col items-center leading-none">
              <span
                className={`font-pos-mono text-[15px] font-bold tabular-nums ${
                  active ? "text-pos-accent-ink" : "text-pos-ink"
                }`}
              >
                {formatQuantity(selectedQty)}
              </span>
              <span className="mt-0.5 font-pos-mono text-[10px] text-pos-muted">
                {t("cancelItem.ofQuantity", { max: formatQuantity(row.quantity) })}
              </span>
            </div>
            <QtyButton
              icon={<PlusOutlined />}
              onClick={() => onQuantityChange(1)}
              disabled={selectedQty >= row.quantity}
            />
          </div>
        )}
      </div>

      {expanded && !row.locked && (
        <RowPercentOptions
          generalPercent={generalPercent}
          active={activeChoice}
          onPick={onPickPercent}
        />
      )}
    </div>
  );
};

/** Процентът на реда: „−15%" (общият), „−20% · ред" (собствен, с рамка),
 *  „−%" на пунктир (без отстъпка; „Без", когато сегашната отпада). Кликът
 *  разгръща лентата с процентите. 32px висок на всички размери — терминалите
 *  в залата са тъчскрийн и с десктоп ширина. */
const PercentChip = ({
  selected,
  percent,
  hasOwnPercent,
  removesDiscount,
  expanded,
  onClick,
  noneLabel,
  ownSuffix,
}: {
  selected: boolean;
  percent: number;
  hasOwnPercent: boolean;
  removesDiscount: boolean;
  expanded: boolean;
  onClick: () => void;
  noneLabel: string;
  ownSuffix: string;
}) => {
  const discounted = selected && percent > 0;
  const Caret = expanded ? CaretUpOutlined : CaretDownOutlined;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      className={`inline-flex h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 font-pos-mono text-[12px] font-bold transition-colors ${
        discounted
          ? `bg-pos-disc-bg text-pos-disc-ink ${
              hasOwnPercent ? "border-pos-disc-ink/60" : "border-transparent"
            }`
          : "border-dashed border-pos-line bg-transparent text-pos-muted hover:border-pos-ctl hover:text-pos-soft2"
      }`}
    >
      {!selected ? (
        <span>{removesDiscount ? noneLabel : "−%"}</span>
      ) : percent === 0 ? (
        <span>{noneLabel}</span>
      ) : (
        <span>
          −{percent}%
          {hasOwnPercent && (
            <span className="font-pos-sans font-semibold opacity-80">
              {" "}
              · {ownSuffix}
            </span>
          )}
        </span>
      )}
      <Caret className="text-[10px]" />
    </button>
  );
};

// Същият бутон като QtyBtn в split/SplitBillModal.tsx — hover: само на
// устройства с истински hover, за да не „залепва" на телефон.
const QtyButton = ({
  icon,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="inline-flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-lg border border-pos-ctl bg-pos-card text-xs text-pos-soft2 transition-colors disabled:cursor-not-allowed disabled:opacity-40 enabled:hover:border-pos-accent/30 enabled:hover:bg-pos-marked enabled:hover:text-pos-accent-ink"
  >
    {icon}
  </button>
);
